import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { STRINGS } from '@/strings/strings';
import { useAuthStore } from '@/stores/authStore';

/**
 * 5a Commit 4 · quiet email-verification reminder in the book shell.
 *
 * Sebastian ruling 2026-08-06: "the book shell carries a quiet line
 * with a resend action. No gate, no blocking, no modal — the customer
 * is stopped at the order anyway, and until then he should be
 * building."
 *
 * ═══ WHY HERE, NOT UPSTREAM ═════════════════════════════════════════
 *
 * Email verification stays required — but the check lives at
 * `src/checkout/checkout.controller.ts:22` (Phase-6 Bestellung,
 * `EmailVerifiedGuard`), not at any book route. A customer who has
 * just handed over photos of his child must reach his title image,
 * not an inbox. The reminder is a quiet nudge inside the shell so he
 * keeps building; the actual gate is at checkout.
 *
 * ═══ WORDING ═══════════════════════════════════════════════════════
 *
 * PLACEHOLDER per Sebastian's correction 2026-08-06: "Turn it
 * around — say what he gets, not what he loses." The Commit-3 draft
 * ("Ohne den kannst du dein Buch später nicht bestellen.") was a
 * threat at the moment the customer had just handed over photos of
 * his child. Placeholder below is in the value-forward register;
 * Sebastian's authorship replaces it.
 *
 * ═══ RESEND ACTION ═══════════════════════════════════════════════════
 *
 * Uses the existing `useAuthStore.requestEmailVerification`
 * (`pwa/src/stores/authStore.ts:43`). Local state gates the button
 * (in-flight + one-shot ack). No error surface: the store's action
 * throws on non-2xx, but the reminder does not attempt a retry UX —
 * a failed resend is silent (returns the customer to the initial
 * state so he can try again with the same button).
 */

export function VerifyReminderLine() {
  const requestEmailVerification = useAuthStore((s) => s.requestEmailVerification);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  // F4 (Sebastian field finding + release 2026-10-16) — the shell
  // banner lives on every BookShell-wrapped route including
  // /figuren. The default copy mentions "dein Buch" which reads
  // wrong on the probe funnel; swap to the probe-scoped string
  // when the current route IS /figuren. All other routes keep the
  // book-side wording.
  const location = useLocation();
  const reminderLine =
    location.pathname === '/figuren'
      ? STRINGS.book.shell.verifyReminderLineProbe
      : STRINGS.book.shell.verifyReminderLine;

  const handleResend = async () => {
    if (status !== 'idle') return;
    setStatus('sending');
    try {
      await requestEmailVerification();
      setStatus('sent');
    } catch {
      setStatus('idle');
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-dream-background border-b border-dream-navy/10 px-5 py-2 text-sm text-dream-navy md:px-10"
    >
      <div className="mx-auto max-w-[1100px]">
        <span>{reminderLine}</span>{' '}
        {status === 'sent' ? (
          <span className="text-dream-navy/70">{STRINGS.book.shell.verifyResentAck}</span>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={status === 'sending'}
            className="text-dream-navy underline focus-visible:outline-2 focus-visible:outline-dream-navy/50 disabled:opacity-50"
          >
            {STRINGS.book.shell.verifyResendCta}
          </button>
        )}
      </div>
    </div>
  );
}
